import { LeanspaceProvider } from "@leanspace/js-client/dist/react";
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs as DateAdapter } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import relativeTimePlugin from "dayjs/plugin/relativeTime";
import { SnackbarProvider } from "notistack";
import React from "react";
import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";

import Login from "./Components/Login";
import Router from "./Components/Router";
import UserContext from "./Contexts/UserContext";
import {getPlatformUrl,getToken} from "./utils";
import {getSession} from "./Utils/Auth";

dayjs.extend(relativeTimePlugin);

const queryClient = new QueryClient();

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#8CD3B4",
    },
    secondary: {
      main: "#645E9D",
    },
    error: {
      main: "#9A031E",
    },
    warning: {
      main: "#E0A458",
    },
  },
});

function App() {
  const [isSessionReady, setSessionReady] = useState(false);
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [token, updateToken] = useState<string>();
  const [didNotFindTenant, setDidNotFindTenant] = useState(false);
  const env = localStorage.getItem("env") as "DEV" | "DEMO" | "PROD";

  useEffect(
    function loadTenantConfig() {
      setDidNotFindTenant(false);
      const load = async () => {
        try {
          setSessionReady(true);

          const session = getSession();
          if (session) {
            updateToken(session.access_token);
            setLoggedIn(true);
          }
        } catch (error) {
          setDidNotFindTenant(true);
        }
      };
      load();
    },
    [env]
  );

  const onLogin = (token: string) => {
    updateToken(token);
    setLoggedIn(true);
  };

  const contextValue = {
    token,
    login: onLogin,
    isLoggedIn,
    env: env || "PROD",
  };

  const baseURL = getPlatformUrl(env);

  return isSessionReady ? (
    <ThemeProvider theme={darkTheme}>
      <UserContext.Provider value={contextValue}>
        <LeanspaceProvider
          clientOptions={{
            getToken,
            baseURL,
          }}
        >
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={DateAdapter}>
              <SnackbarProvider maxSnack={3}>
                <CssBaseline />
                {isLoggedIn ? <Router /> : <Login />}
              </SnackbarProvider>
            </LocalizationProvider>
          </QueryClientProvider>
        </LeanspaceProvider>
      </UserContext.Provider>
    </ThemeProvider>
  ) : didNotFindTenant ? (
    <>Failed to load tenant information — did you update the config.js file?</>
  ) : null;
}

export default App;
